/**
 * @fileoverview Context for the modal API.
 * This context provides a way to open and close modals.
 */

import { PropsWithChildren, createContext, useState } from 'react'
import { CSSTransition } from 'react-transition-group'
import { Modal } from './Modal'
import * as css from './Modal.module.css'

/**
 * @interface ModalConfig
 * @description The configuration for a modal.
 * @property {JSX.Element} content - The content of the modal.
 */
export interface ModalConfig {
    content: JSX.Element
}

/**
 * @interface ModalAPI
 * @description The API for the modal context.
 * @property {function} open - The function to open a modal.
 * @property {function} close - The function to close a modal.
 */
export interface ModalAPI {
    open: (config: ModalConfig) => void
    close: () => void
}

export const ModalContext = createContext<ModalAPI>(undefined)

/**
 * @function ModalAPIContext
 * @description The context provider for the modal API.
 * @param {PropsWithChildren} children - The children to render.
 * @returns {JSX.Element} - A React component that renders the modal API context.
 */
export function ModalAPIContext({ children }: PropsWithChildren) {
    const [modalConfig, setModalConfig] = useState<ModalConfig>({
        content: null,
    })

    const [isOpen, setIsOpen] = useState<boolean>(false)

    const API: ModalAPI = {
        async open(modalConfig: ModalConfig) {
            setModalConfig(modalConfig)
            setIsOpen(true)
        },
        async close() {
            setIsOpen(false)
        },
    }

    return (
        <ModalContext.Provider value={API}>
            {children}
            <CSSTransition
                in={isOpen}
                timeout={300}
                mountOnEnter
                unmountOnExit
                classNames={{
                    enter: css.transition_enter,
                    enterActive: css.transition_enter_active,
                    exit: css.transition_exit,
                    exitActive: css.transition_exit_active,
                }}
            >
                <Modal content={modalConfig.content} />
            </CSSTransition>
        </ModalContext.Provider>
    )
}
